/**
 * Role Assignment API Service
 *
 * Handles Azure RBAC role assignment operations via the ARM control plane.
 * Used during Foundry agent connection creation to automatically grant
 * the Logic App's managed identity the permissions it needs to access
 * Foundry agents on the Cognitive Services account.
 *
 * The key role is "Azure AI Developer" (64702f94-c441-49e6-a78b-ef80e0188fee)
 * which includes the data action:
 *   Microsoft.CognitiveServices/accounts/AIServices/agents/read
 *
 * Without this role, the runtime gets:
 *   HTTP 401 PermissionDenied — principal lacks required data action
 */

import { buildAcaArmUrl } from './acaAppUrl';

// =============================================================================
// CONSTANTS
// =============================================================================

import { getArmBaseUrl, isArmMockMode } from './armConfig';

const AUTHORIZATION_API_VERSION = '2022-04-01';

/**
 * Well-known Azure built-in role definition GUIDs.
 *
 * "Azure AI Developer" (64702f94) includes:
 *   - Microsoft.MachineLearningServices/workspaces/agents/action
 *   - Microsoft.CognitiveServices/accounts/AIServices/agents/read
 *   which are required for Foundry agent invocation from the runtime.
 *
 * "Azure AI User" (53ca6127) covers CognitiveServices data actions but
 * does NOT cover MachineLearningServices/workspaces/agents/action.
 */
const AZURE_AI_DEVELOPER_ROLE_ID = '64702f94-c441-49e6-a78b-ef80e0188fee';

// =============================================================================
// TYPES
// =============================================================================

export interface RoleAssignmentResult {
  /** Whether the role assignment succeeded or was already in place */
  success: boolean;
  /** True if the role was already assigned (no action taken) */
  alreadyAssigned: boolean;
  /** Error message if the assignment failed */
  error?: string | undefined;
  /** Error code from the API */
  errorCode?: string | undefined;
}

interface SiteIdentity {
  principalId: string;
  tenantId: string;
  type: string;
}

interface SiteResponse {
  identity?: SiteIdentity;
}

interface RoleAssignmentProperties {
  roleDefinitionId: string;
  principalId: string;
  principalType?: string;
  scope?: string;
}

interface RoleAssignment {
  id: string;
  name: string;
  properties: RoleAssignmentProperties;
}

interface RoleAssignmentListResponse {
  value: RoleAssignment[];
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Make an authenticated ARM request.
 */
async function armFetch<T>(
  url: string,
  accessToken: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (!isArmMockMode()) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData: { error?: { code?: string; message?: string } } | null = null;
    try {
      errorData = await response.json();
    } catch {
      // Response might not be JSON
    }

    const error = new Error(
      errorData?.error?.message ?? `ARM request failed: ${response.status}`
    ) as Error & { code?: string | undefined; status?: number };
    error.code = errorData?.error?.code ?? undefined;
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Extract the Cognitive Services account resource ID from a project resource ID.
 *
 * Input:  /subscriptions/.../providers/Microsoft.CognitiveServices/accounts/{name}/projects/{proj}
 * Output: /subscriptions/.../providers/Microsoft.CognitiveServices/accounts/{name}
 */
function extractAccountResourceId(projectOrResourceId: string): string {
  // If it already ends at /accounts/{name} (no /projects/), return as-is
  const accountsIdx = projectOrResourceId.toLowerCase().indexOf('/accounts/');
  if (accountsIdx === -1) {
    return projectOrResourceId;
  }

  const afterAccounts = projectOrResourceId.substring(accountsIdx + '/accounts/'.length);
  const accountName = afterAccounts.split('/')[0];
  return projectOrResourceId.substring(0, accountsIdx) + '/accounts/' + accountName;
}

/**
 * Extract subscription ID from an ARM resource ID.
 */
function extractSubscriptionId(resourceId: string): string | undefined {
  const match = resourceId.match(/\/subscriptions\/([^/]+)/i);
  return match?.[1];
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Get the Logic App site's system-assigned managed identity principal ID.
 */
export async function getLogicAppIdentity(
  subscriptionId: string,
  resourceGroup: string,
  appName: string,
  accessToken: string
): Promise<SiteIdentity | null> {
  const url = `${buildAcaArmUrl(subscriptionId, resourceGroup, appName)}?api-version=2024-03-01`;

  try {
    const site = await armFetch<SiteResponse>(url, accessToken);
    return site.identity ?? null;
  } catch (err) {
    console.warn('Failed to fetch Logic App identity:', err);
    return null;
  }
}

/**
 * Check if a role assignment already exists for a given principal on a scope.
 */
export async function checkExistingRoleAssignment(
  scope: string,
  principalId: string,
  roleDefinitionGuid: string,
  accessToken: string
): Promise<boolean> {
  const subscriptionId = extractSubscriptionId(scope);
  if (!subscriptionId) return false;

  const roleDefinitionId = `/subscriptions/${subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionGuid}`;
  const filter = encodeURIComponent(
    `principalId eq '${principalId}' and roleDefinitionId eq '${roleDefinitionId}'`
  );
  const url = `${getArmBaseUrl()}${scope}/providers/Microsoft.Authorization/roleAssignments?api-version=${AUTHORIZATION_API_VERSION}&$filter=${filter}`;

  try {
    const result = await armFetch<RoleAssignmentListResponse>(url, accessToken);
    return (result.value?.length ?? 0) > 0;
  } catch {
    // If we can't check, assume not assigned (will try to assign)
    return false;
  }
}

/**
 * Create a role assignment on a resource scope.
 */
export async function createRoleAssignment(
  scope: string,
  principalId: string,
  roleDefinitionGuid: string,
  accessToken: string
): Promise<void> {
  const subscriptionId = extractSubscriptionId(scope);
  if (!subscriptionId) {
    throw new Error('Cannot extract subscription ID from scope');
  }

  const roleAssignmentName = crypto.randomUUID();
  const roleDefinitionId = `/subscriptions/${subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionGuid}`;

  const url = `${getArmBaseUrl()}${scope}/providers/Microsoft.Authorization/roleAssignments/${roleAssignmentName}?api-version=${AUTHORIZATION_API_VERSION}`;

  await armFetch(url, accessToken, {
    method: 'PUT',
    body: JSON.stringify({
      properties: {
        roleDefinitionId,
        principalId,
        principalType: 'ServicePrincipal',
      },
    }),
  });
}

/**
 * Ensure the Logic App's managed identity has the "Azure AI Developer" role
 * on the Cognitive Services account that backs the Foundry project.
 *
 * This is the main function called during Foundry connection creation.
 * It:
 *   1. Fetches the Logic App's system-assigned managed identity principal ID
 *   2. Checks if the "Azure AI Developer" role is already assigned on the account
 *   3. If not, creates the role assignment via ARM PUT
 *
 * @param subscriptionId - Logic App's subscription
 * @param resourceGroup  - Logic App's resource group
 * @param appName        - Logic App site name
 * @param projectResourceId - Full ARM ID of the Foundry project (or account)
 * @param accessToken    - User's ARM bearer token
 */
export async function ensureFoundryPermissions(
  subscriptionId: string,
  resourceGroup: string,
  appName: string,
  projectResourceId: string,
  accessToken: string
): Promise<RoleAssignmentResult> {
  try {
    // Step 1: Get the Logic App's managed identity
    const identity = await getLogicAppIdentity(
      subscriptionId,
      resourceGroup,
      appName,
      accessToken
    );

    if (!identity?.principalId) {
      return {
        success: false,
        alreadyAssigned: false,
        error:
          'Logic App does not have a system-assigned managed identity enabled. ' +
          'Please enable it in the Logic App Identity settings.',
        errorCode: 'NoManagedIdentity',
      };
    }

    // Step 2: Determine the scope (Cognitive Services account, not project)
    const accountScope = extractAccountResourceId(projectResourceId);

    // Step 3: Check if already assigned
    const alreadyAssigned = await checkExistingRoleAssignment(
      accountScope,
      identity.principalId,
      AZURE_AI_DEVELOPER_ROLE_ID,
      accessToken
    );

    if (alreadyAssigned) {
      return { success: true, alreadyAssigned: true };
    }

    // Step 4: Create the role assignment
    await createRoleAssignment(
      accountScope,
      identity.principalId,
      AZURE_AI_DEVELOPER_ROLE_ID,
      accessToken
    );

    return { success: true, alreadyAssigned: false };
  } catch (err) {
    const error = err as Error & { code?: string; status?: number };

    // RoleAssignmentExists means it's already assigned (race condition or filter miss)
    if (error.code === 'RoleAssignmentExists') {
      return { success: true, alreadyAssigned: true };
    }

    // AuthorizationFailed means the current user doesn't have permission to assign roles
    if (error.code === 'AuthorizationFailed' || error.status === 403) {
      return {
        success: false,
        alreadyAssigned: false,
        error:
          'You do not have permission to assign roles on this resource. ' +
          'Please ask an Owner or User Access Administrator to grant the ' +
          '"Azure AI Developer" role to your Logic App\'s managed identity ' +
          `on the Cognitive Services account.`,
        errorCode: 'InsufficientPermissions',
      };
    }

    return {
      success: false,
      alreadyAssigned: false,
      error: error.message ?? 'Failed to assign permissions',
      errorCode: error.code,
    };
  }
}
